/* global google */
import { useEffect, useRef, useState } from "react";
import "./google-btn.css";
import {
    setLogin,
    setSubscriptionStatus,
    setOnboardingState,
} from "../../../state";
import { useDispatch } from "react-redux";
import { trackEvent } from "../../../utils/pixel.js";
import { Spinner } from "@heroui/react";
import { collectSignupAttribution } from "../../../utils/captureMetaParams";

export const GoogleButton = ({ setServerError, setCanRestoreMessage }) => {
    const dispatch = useDispatch();
    const codeClientRef = useRef(null);
    const isHandlingRef = useRef(false);
    const [isLoading, setIsLoading] = useState(false);

    const PRESENTATION_MODE = process.env.REACT_APP_PRESENTATION_MODE === 'true' ?? false

    useEffect(() => {
        if (PRESENTATION_MODE) {
            // setServerError("Sorry, aome stopped its activity. Bye bye")
            return
        }

        if (!window.google?.accounts?.oauth2) return;

        codeClientRef.current = google.accounts.oauth2.initCodeClient({
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
            scope: "openid email profile",
            ux_mode: "popup",
            callback: async (response) => {
                if (!response.code) return;

                await handleSignupOAuth(response.code);
            },
        });
    }, []);

    const handleSignupOAuth = async (code) => {
        if (PRESENTATION_MODE) {
            setServerError("Sorry, aome stopped its activity. Bye bye")
            return
        }
        if (isHandlingRef.current) return;
        isHandlingRef.current = true;

        setIsLoading(true)
        try {
            const inviteToken = sessionStorage.getItem("inviteToken");
            const signupAttribution = collectSignupAttribution()

            const res = await fetch(
                `${process.env.REACT_APP_API_BASE}/auth/oauth-callback`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({
                        code,
                        inviteToken,

                        adId: signupAttribution.adId,
                        fbp: signupAttribution.fbp,
                        fbc: signupAttribution.fbc
                    }),
                }
            );

            const data = await res.json();

            if (!data || typeof data !== 'object') {
                setServerError("サインアップ中にエラーが発生しました。もう一度お試しください。");
                return;
            }

            if (!res.ok) {
                setServerError(data.messageJp ?? "サインアップ中にエラーが発生しました。もう一度お試しください。");
                return;
            }

            const {
                accessToken,
                expiresAt,
                userData,
                isInvited,
                canRestore, messageJp
            } = data;

            if (canRestore) {
                if (messageJp) {
                    setCanRestoreMessage(messageJp)
                } else {
                    setCanRestoreMessage("アカウントは削除されています。復元するには、このアカウントでログインしてください。")
                }
                return
            }

            if (!accessToken || !expiresAt || !userData) {
                setServerError("サインアップ中にエラーが発生しました。もう一度お試しください。");
                return;
            }
            dispatch(
                setSubscriptionStatus({
                    subscriptionStatus: "trialing"
                })
            )
            dispatch(
                setLogin({
                    user: userData,
                    token: accessToken,
                    expiresAt,
                })
            );
            dispatch(setOnboardingState({ onboardingStep: "pre-onboarding" }))
        } catch (error) {
            setServerError("サインアップ中にエラーが発生しました。もう一度お試しください。");
        } finally {
            setIsLoading(false)
            isHandlingRef.current = false;
        }
    }

    const handleGoogleClick = () => {
        if (PRESENTATION_MODE) {
            setServerError("Sorry, aome stopped its activity. Bye bye")
            return
        }

        codeClientRef.current?.requestCode(); // opens popup
    };

    return (
        <button
            type="button"
            onClick={handleGoogleClick}
            disabled={isLoading}
            className="disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-full flex items-center justify-center gap-2 rounded-md border px-4 py-2 text-[#1f1f1f] text-sm font-light bg-white/90 hover:bg-gray-100 transition-colors font-geologica"
        >
            {isLoading &&
                <Spinner color="gray-900" size="sm" />
            }

            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" style={{ display: "block" }} width={20} height={20}>
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                <path fill="none" d="M0 0h48v48H0z"></path>
            </svg>
            <span>
                Google
                <span className="font-light">で続行</span>
            </span>

        </button>
    );
};
